import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import errorReporter from '../utils/errorReporting';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Report the error to Firebase Analytics
    errorReporter.reportUIError(error, this.props.component || 'ErrorBoundary', 'component_crash', {
      component_stack: JSON.stringify(errorInfo.componentStack).substring(0, 500),
      props: JSON.stringify(this.props).substring(0, 200)
    });

    // Store error info for display
    this.setState({
      error,
      errorInfo
    });

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: { xs: '200px', sm: '300px' },
            p: 2
          }}
        >
          <Card 
            sx={{ 
              maxWidth: 500, 
              width: '100%',
              boxShadow: 3
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <ErrorOutlineIcon 
                sx={{ 
                  fontSize: 64, 
                  color: 'error.main', 
                  mb: 2 
                }} 
              />
              
              <Typography variant="h5" gutterBottom>
                Oops! Something went wrong
              </Typography>
              
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                {this.props.fallbackMessage || 
                  "We've encountered an unexpected error. This issue has been automatically reported to our team."
                }
              </Typography>

              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ 
                  textAlign: 'left', 
                  bgcolor: 'grey.100', 
                  p: 2, 
                  borderRadius: 1, 
                  mb: 3,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem' }}>
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack.substring(0, 500)}`}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button 
                  variant="outlined" 
                  onClick={this.handleRetry}
                  size="large"
                >
                  Try Again
                </Button>
                <Button 
                  variant="contained" 
                  onClick={this.handleReload}
                  size="large"
                >
                  Reload Page
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easy wrapping
export const withErrorBoundary = (Component, options = {}) => {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary 
        component={options.component || Component.name}
        fallbackMessage={options.fallbackMessage}
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;